import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";

// Server-proxied Google Places Autocomplete.
// Fetches predictions from /grade/autocomplete and
// updates a hidden place_id field on selection.
export default class extends Controller {
  static targets = ["input", "results", "placeId"];
  static values = { endpoint: String };

  connect() {
    this.sessionToken = this.generateSessionToken();
    this.search = debounce(this.search.bind(this), 250);
    this.boundOutsideClick = this.onOutsideClick.bind(this);
    document.addEventListener("click", this.boundOutsideClick);
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick);
  }

  generateSessionToken() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) +
      Date.now().toString(36);
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults();
    }
  }

  async search() {
    const input = this.inputTarget.value.trim();
    if (input.length < 2) {
      this.hideResults();
      return;
    }
    const url =
      `${this.endpointValue}?` +
      `input=${encodeURIComponent(input)}` +
      `&session_token=${this.sessionToken}`;
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const data = await response.json();
      this.renderResults(data.suggestions || []);
    } catch (e) {
      // swallow: don't break the page
    }
  }

  renderResults(suggestions) {
    this.resultsTarget.innerHTML = "";
    if (suggestions.length === 0) {
      this.hideResults();
      return;
    }
    suggestions.forEach((s) => {
      const placePrediction = s.placePrediction;
      if (!placePrediction) return;
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "text-left w-full p-2 hover:bg-base-200";
      button.textContent =
        placePrediction.text?.text ||
        placePrediction.structuredFormat?.mainText?.text ||
        "";
      button.dataset.placeId = placePrediction.placeId;
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.select(placePrediction);
      });
      item.appendChild(button);
      this.resultsTarget.appendChild(item);
    });
    this.resultsTarget.classList.remove("hidden");
  }

  select(placePrediction) {
    this.placeIdTarget.value = placePrediction.placeId;
    this.inputTarget.value =
      placePrediction.text?.text ||
      placePrediction.structuredFormat?.mainText?.text ||
      "";
    this.hideResults();
    if (typeof this.element.requestSubmit === "function") {
      this.element.requestSubmit();
    } else if (typeof this.element.submit === "function") {
      this.element.submit();
    }
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden");
  }
}
